import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TargetProgressBar } from "@/components/TargetProgress";
import { Plus, X, Copy, Target as TargetIcon, Upload } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "company", label: "Company" },
  { value: "state", label: "State" },
  { value: "territory", label: "Territory" },
  { value: "agent", label: "Agent" },
  { value: "customer", label: "Customer" },
  { value: "product", label: "Product Category" },
];
const PERIODS = ["monthly", "quarterly", "fy", "cy", "custom"];
const STATUSES = ["Draft", "Active", "Completed", "Cancelled"];

export default function SalesTargets() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "management", "general_manager"].includes(user?.role);
  const [targets, setTargets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [agents, setAgents] = useState([]);
  const [statusF, setStatusF] = useState("");

  const load = async () => {
    const [tRes, sRes] = await Promise.all([
      api.get("/targets", { params: statusF ? { status: statusF } : {} }),
      api.get("/targets/summary"),
    ]);
    setTargets(tRes.data);
    setSummary(sRes.data);
  };
  useEffect(() => { load(); api.get("/agents").then(r => setAgents(r.data)).catch(() => {}); }, [statusF]);

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Sales Targets</h1>
          <p className="text-slate-600 mt-1">Progress against active targets — pace, forecast and remaining amount</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="target-status-filter">
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <button onClick={() => setShowCopy(true)} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="copy-targets-btn">
              <Copy className="w-4 h-4" /> Copy Period
            </button>
            <button onClick={() => setShowImport(true)} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="import-targets-btn">
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <button onClick={() => setShowNew(true)} className="mlily-btn h-11 px-5 rounded-lg font-semibold text-sm flex items-center gap-2" data-testid="new-target-btn">
              <Plus className="w-4 h-4" /> New Target
            </button>
          </div>
        )}
      </div>

      {/* Active summary */}
      <div className="space-y-4">
        {summary.map(p => (
          <div key={p.target_id} className="bg-white rounded-xl border border-slate-200 p-5">
            <TargetProgressBar progress={p} />
          </div>
        ))}
        {summary.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">No active targets in this period.</div>}
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="font-heading font-semibold text-[#001F54] flex items-center gap-2">
              <TargetIcon className="w-4 h-4 text-[#003DA5]" /> All Targets ({targets.length})
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-right px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {targets.map(t => (
                <tr key={t.id} className="border-t hover:bg-slate-50" data-testid={`target-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3">{TYPES.find(x => x.value === t.target_type)?.label || t.target_type}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t.start_date} → {t.end_date}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(t.target_value)}</td>
                  <td className="px-4 py-3"><span className="text-xs badge-info px-2 py-1 rounded-full">{t.status}</span></td>
                </tr>
              ))}
              {targets.length === 0 && <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">No targets yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewTargetModal agents={agents} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {showCopy && <CopyModal onClose={() => setShowCopy(false)} onSaved={() => { setShowCopy(false); load(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onSaved={() => { setShowImport(false); load(); }} />}
    </div>
  );
}

function ImportModal({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV/XLSX file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/targets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.imported} target${data.imported === 1 ? "" : "s"} imported`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">Import Targets (CSV / XLSX)</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-600">
            Expected columns (any order, case-insensitive):
            <div className="mt-2 p-3 bg-[#F3F7FC] rounded-lg border font-mono text-xs">
              name, agent_name (or email/agent_id), start_date, end_date, target_value, period, notes
            </div>
          </div>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" data-testid="target-import-file" />
          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="font-semibold text-emerald-700">Imported {result.imported} of {result.total_rows}</div>
              {result.invalid?.length > 0 && <div className="text-xs text-red-700 mt-1">{result.invalid.length} row(s) invalid</div>}
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Close</button>
          <button type="submit" disabled={busy} className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="run-import-targets-btn">
            {busy ? "Importing…" : "Run Import"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NewTargetModal({ agents, onClose, onSaved }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [f, setF] = useState({
    name: "", target_type: "agent", scope: "", period: "monthly",
    start_date: monthStart, end_date: monthEnd, target_value: 100000,
    sales_measurement: "net_ex_gst", status: "Active", notes: "",
  });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/targets", { ...f, target_value: Number(f.target_value) || 0 });
      toast.success("Target created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between sticky top-0 bg-white"><h3 className="text-xl font-heading font-semibold">New Sales Target</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <F label="Target Name *"><input required value={f.name} onChange={upd("name")} className="w-full h-11 px-3 border rounded-lg" data-testid="target-name" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Type"><select value={f.target_type} onChange={upd("target_type")} className="w-full h-11 px-3 border rounded-lg">{TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></F>
            <F label="Period"><select value={f.period} onChange={upd("period")} className="w-full h-11 px-3 border rounded-lg">{PERIODS.map(p => <option key={p} value={p}>{p}</option>)}</select></F>
          </div>
          {f.target_type === "agent" && (
            <F label="Agent"><select required value={f.scope} onChange={upd("scope")} className="w-full h-11 px-3 border rounded-lg" data-testid="target-agent"><option value="">Choose agent…</option>{agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></F>
          )}
          {f.target_type === "state" && <F label="State (e.g. NSW)"><input value={f.scope} onChange={upd("scope")} className="w-full h-11 px-3 border rounded-lg" /></F>}
          {(f.target_type === "customer" || f.target_type === "product") && <F label="Scope value"><input value={f.scope} onChange={upd("scope")} className="w-full h-11 px-3 border rounded-lg" /></F>}
          <div className="grid grid-cols-2 gap-3">
            <F label="Start Date"><input type="date" value={f.start_date} onChange={upd("start_date")} className="w-full h-11 px-3 border rounded-lg" /></F>
            <F label="End Date"><input type="date" value={f.end_date} onChange={upd("end_date")} className="w-full h-11 px-3 border rounded-lg" /></F>
          </div>
          <F label="Target Value (AUD) *"><input required type="number" value={f.target_value} onChange={upd("target_value")} className="w-full h-11 px-3 border rounded-lg" data-testid="target-value" /></F>
          <F label="Notes"><textarea value={f.notes} onChange={upd("notes")} rows={2} className="w-full p-3 border rounded-lg" /></F>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="save-target-btn">Save Target</button></div>
      </form>
    </div>
  );
}

function CopyModal({ onClose, onSaved }) {
  const today = new Date();
  const lastStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10);
  const lastEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10);
  const nextStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const nextEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [f, setF] = useState({ source_start: lastStart, source_end: lastEnd, new_start: nextStart, new_end: nextEnd, adjust_pct: 0 });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/targets/copy", { ...f, adjust_pct: Number(f.adjust_pct) || 0 });
      toast.success(`Copied ${data.copied} target${data.copied === 1 ? "" : "s"}`);
      onSaved();
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-md">
        <div className="p-5 border-b flex justify-between"><h3 className="text-xl font-heading font-semibold">Copy Targets</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">Copies all targets from the source period to a new period, optionally scaled by a percentage.</p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Source Start"><input type="date" value={f.source_start} onChange={upd("source_start")} className="w-full h-11 px-3 border rounded-lg" /></F>
            <F label="Source End"><input type="date" value={f.source_end} onChange={upd("source_end")} className="w-full h-11 px-3 border rounded-lg" /></F>
            <F label="New Start"><input type="date" value={f.new_start} onChange={upd("new_start")} className="w-full h-11 px-3 border rounded-lg" /></F>
            <F label="New End"><input type="date" value={f.new_end} onChange={upd("new_end")} className="w-full h-11 px-3 border rounded-lg" /></F>
          </div>
          <F label="Adjust by % (e.g. 5 for +5%)"><input type="number" step="0.1" value={f.adjust_pct} onChange={upd("adjust_pct")} className="w-full h-11 px-3 border rounded-lg" data-testid="adjust-pct" /></F>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button type="button" onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button><button type="submit" className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="run-copy-btn">Copy</button></div>
      </form>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
